import React from 'react';
import {
  ActivityIcon,
  AlertCircleIcon,
  AwardIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardListIcon,
  CreditCardIcon,
  HeartPulseIcon,
  PillIcon,
  ReorderIcon,
  RotateCcwIcon,
  ShieldIcon,
  StarIcon,
  StethoscopeIcon,
  TruckIcon,
  UsersPlusIcon,
  ZapIcon,
} from '../icons/DesignSystemIcons';

/** 10px radius, blue-100 fill, no border — matches overview DS tile spec. */
function DsIconTile({ children, className = '' }) {
  return (
    <span
      className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-blue-100 text-blue-800 ${className}`}
      aria-hidden
    >
      {children}
    </span>
  );
}

const SECTION_ICON_MAP = {
  health_checks: ClipboardListIcon,
  consultations: StethoscopeIcon,
  dental: PillIcon,
  vision: ActivityIcon,
  hra: HeartPulseIcon,
  pre_employment: UsersPlusIcon,
  steps: ZapIcon,
  health_challenges: AwardIcon,
  wellness_calendar: CalendarIcon,
  health_points: StarIcon,
  /** Utilization / metric cards — DS tile keys beyond overview service cards */
  pharmacy: PillIcon,
  pharmacy_delivery: TruckIcon,
  pharmacy_tat: RotateCcwIcon,
  gym: ActivityIcon,
  wallet: CreditCardIcon,
  vaccination: ShieldIcon,
  upcoming_appointments: CalendarIcon,
  metric_employees_utilized: UsersPlusIcon,
  metric_repeat_users: ReorderIcon,
};

/**
 * Maps DynamicBenefitPage `benefitType` route param to OverviewSectionIcon `name`.
 */
export function utilizationBenefitTypeToOverviewIconKey(benefitType) {
  const map = {
    ahc: 'health_checks',
    dental: 'dental',
    vision: 'vision',
    covid_vaccination: 'vaccination',
    flu_vaccination: 'vaccination',
    specialist_consultation: 'consultations',
    inclinic_consultation: 'consultations',
    occupational_medical_rooms: 'consultations',
    cancer_screening: 'health_checks',
    fertility_treatment: 'hra',
    pre_employment: 'pre_employment',
  };
  return map[benefitType] || 'health_checks';
}

/**
 * Benefit / overview section mark: DS glyph inside blue-100 rounded tile.
 * @param {string} name — key from SERVICE_CARD_DETAILS / NOT_ENABLED_BENEFITS
 */
export function OverviewSectionIcon({ name, className = '' }) {
  const Icon = SECTION_ICON_MAP[name] || ClipboardListIcon;
  return (
    <span className={className}>
      <DsIconTile>
        <Icon className='h-5 w-5' strokeWidth='1.75' />
      </DsIconTile>
    </span>
  );
}

/** Rating star (amber); stroke glyph from design system set. */
export function OverviewRatingStar({ className = 'h-3.5 w-3.5 shrink-0 text-amber-500' }) {
  return <StarIcon className={className} strokeWidth='1.75' aria-hidden />;
}

/** “Not enabled” bar — shield metaphor; no blue tile (amber strip context). */
export function OverviewLockIcon({ className = 'ml-6 h-8 w-8 shrink-0 text-amber-800' }) {
  return <ShieldIcon className={className} strokeWidth='1.75' aria-hidden />;
}

export function OverviewChevronIcon({ open, className = 'mr-6 h-6 w-6 shrink-0 text-gray-600' }) {
  return (
    <span className={`${className} inline-flex transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden>
      <ChevronDownIcon className='h-6 w-6' strokeWidth='1.75' />
    </span>
  );
}

export function OverviewTrendIcon({ up, className = 'h-3.5 w-3.5 shrink-0' }) {
  const color = up ? 'text-teal-700' : 'text-red-600';
  if (up) {
    return <ChevronUpIcon className={`${className} ${color}`} strokeWidth={2} aria-hidden />;
  }
  return <ChevronDownIcon className={`${className} ${color}`} strokeWidth={2} aria-hidden />;
}

export function OverviewCalendarEmptyIllustration({ className = 'mx-auto h-28 w-28 text-gray-300' }) {
  return <CalendarIcon className={className} strokeWidth='1.25' aria-hidden />;
}

export function OverviewInfoBannerIcon({ className = 'h-6 w-6 shrink-0 text-amber-600' }) {
  return <AlertCircleIcon className={className} strokeWidth='1.75' aria-hidden />;
}
